import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft as BackIcon } from '@material-ui/icons';
import {
    makeStyles,
    Grid,
    Typography,
    Paper,
    TableContainer,
    Box,
    Button,
    Tooltip,
    IconButton,
} from '@material-ui/core';
import { useAccessTokens } from '../../Authenticate';
import MaterialTable from 'material-table';
import { apiGet, apiDelete } from '../../api';
import NavLink from '../../controls/NavLink';
import DeleteIcon from '@material-ui/icons/Delete';
import { MTableBodyRow } from 'material-table';
import red from '@material-ui/core/colors/red';
import { getFullName } from '../../util';

const useStyles = makeStyles((theme) => ({
    backButton: {
        display: 'inline-flex',
        flexDirection: 'row',
        alignItems: 'center',
        color: '#484848',
        lineHeight: '24px',
        marginLeft: -7,
    },
    entityName: {
        fontSize: 36,
        color: '#333333',
        letterSpacing: ' -0.56px',
        lineHeight: '44px',
    },
    linkedProfiles: {
        color: '#333333',
        fontSize: 24,
        fontWeight: 500,
        letterSpacing: '0',
        lineHeight: '32px',
    },
    removedText: {
        color: red[700],
        paddingLeft: theme.spacing(1),
    },
    disableText: {
        color: '#666666',
    },
    deleteIcon: {
        color: 'rgba(0,0,0,0.54)',
    },
    contentSpacer: {
        marginTop: theme.spacing(3),
        [theme.breakpoints.up('sm')]: {
            marginTop: theme.spacing(10),
        },
    },
}));

const LinkedProfiles = ({ location }) => {
    const classes = useStyles();
    const { getAccessToken, authorizationScopes } = useAccessTokens();
    const [linkedProfiles, setLinkedProfiles] = useState([]);
    const [selectedProfiles, setSelectedProfiles] = useState([]);
    const [rowHover, setRowHover] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectionMade, setSelectionMade] = useState(false);
    const profilesRef = useRef(null);
    const tableRef = useRef(null);
    const { person } = location.state;
    const [result] = useState(location.state.result);

    const handleUnlinkProfiles = () => {
        setLinkedProfiles(
            linkedProfiles.map((profile) => {
                const data = selectedProfiles.find((selectedProfile) => selectedProfile.id === profile.id);
                return data ? { ...data, removed: true } : profile;
            }),
        );
        setSelectedProfiles([]);
        setSelectionMade(true);
        tableRef.current.onAllSelected(false);
    };

    const handleUnlinkProfile = (profileId) => {
        setLinkedProfiles(
            linkedProfiles.map((profile) => {
                return profile.id === profileId ? { ...profile, removed: true } : profile;
            }),
        );
        setSelectionMade(true);
        tableRef.current.onAllSelected(false);
    };

    const handleUndoProfile = (rowData) => {
        setLinkedProfiles(
            linkedProfiles.map((profile) => {
                return profile.id === rowData.id ? { ...profile, removed: false } : profile;
            }),
        );
        setSelectionMade(false);
    };

    const handleDelete = async () => {
        const profiles = profilesRef.current;
         for (const profile of profiles) {
            if (profile.removed) {
                const token = await getAccessToken(authorizationScopes.persons);

                await apiDelete(`persons/${person.id}/registeredUsers/${profile.id}`, token);
            }
        }
        window.location.reload();
    };

    useEffect(() => {
        profilesRef.current = linkedProfiles;
    }, [linkedProfiles]);

    useEffect(() => {
        (async function () {
            try {
                setLoading(true);
                const token = await getAccessToken(authorizationScopes.persons);
                if (person) {
                    const { data } = await apiGet(`persons/${person.id}/registeredUsers`, token);
                    setLinkedProfiles(data.map((profile) => ({ ...profile, removed: false })));
                } else {
                    const { results } = await apiGet(`persons/${result[0].id}/registeredUsers`, token);
                    setLinkedProfiles(results.map((profile) => ({ ...profile, removed: false })));
                }
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error(err);
            } finally {
                setLoading(false);
            }
        })();
        return () => {
            (async function () {
                const profiles = profilesRef.current;
                for (const profile of profiles) {
                    if (profile.removed) {
                        const token = await getAccessToken(authorizationScopes.persons);

                        if (person.id) {
                            await apiDelete(`persons/${person.id}/registeredUsers/${profile.id}`, token);
                        } else {
                            await apiDelete(`persons/${result[0].id}/registeredUsers/${profile.id}`, token);
                        }
                    }
                }
            })();
        };
    }, [setLoading, setLinkedProfiles, getAccessToken, authorizationScopes, person, result]);

    return (
        <>
            <Grid container item xs={12} spacing={4} justifyContent="center" alignItems="center" direction="column">
                <div className={classes.contentSpacer} />
                <Grid item xs={12} sm={8} container spacing={1} direction="column">
                    <Grid item>
                        <NavLink to="/" variant="subtitle1">
                            <span className={classes.backButton}>
                                <BackIcon />
                                Find Licensee
                            </span>
                        </NavLink>
                    </Grid>
                    <Grid item>
                        {person &&<Typography className={classes.entityName}>{getFullName(person.name)}</Typography>}
                        {result &&<Typography className={classes.entityName}>{result.name}</Typography>}
                    </Grid>
                    <Grid item>
                        <Grid container justifyContent="space-between" alignItems="center">
                            <Grid item>
                                <Box pt={3} pb={1}>
                                    <Typography className={classes.linkedProfiles}>Linked Profiles</Typography>
                                </Box>
                            </Grid>
                            {selectedProfiles.length > 0 && (
                                <Grid item>
                                    <Tooltip
                                        title="Remove the selected profiles linked to the licensee."
                                        placement="top"
                                    >
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            size="small"
                                            onClick={handleUnlinkProfiles}
                                        >
                                            Revoke Link
                                        </Button>
                                    </Tooltip>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                    <Grid item>
                        <TableContainer component={Paper}>
                            <MaterialTable
                                tableRef={tableRef}
                                options={{
                                    toolbar: false,
                                    paging: false,
                                    draggable: false,
                                    header: false,
                                    actionsColumnIndex: 1,
                                    selectionProps: (rowData) => ({
                                        color: 'primary',
                                        disabled: rowData.removed,
                                    }),
                                    rowStyle: (rowData) => ({
                                        backgroundColor:
                                            rowHover === rowData.tableData.id && !rowData.removed && 'rgba(0,0,0,0.04)',
                                    }),
                                }}
                                columns={[
                                    {
                                        title: 'Name',
                                        field: 'name',
                                        render: function customRender(rowData) {
                                            return (
                                                <Typography className={rowData.removed && classes.disableText}>
                                                    {rowData.name}
                                                    {rowData.removed && (
                                                        <span className={classes.removedText}>SELECTED FOR REMOVAL</span>
                                                    )}
                                                </Typography>
                                            );
                                        },
                                    },
                                ]}
                                actions={[
                                    {
                                        icon: 'revoke',
                                        tooltip: 'Revoke link',
                                    },
                                ]}
                                noDataMessage="No Linked Profiles"
                                data={linkedProfiles}
                                onSelectionChange={setSelectedProfiles}
                                components={{
                                    Container: function ContainerComponent({ children }) {
                                        return <div>{children}</div>;
                                    },
                                    Row: function RowComponent(props) {
                                        return (
                                            <MTableBodyRow
                                                {...{
                                                    ...props,
                                                    options: {
                                                        ...props.options,
                                                        selection: true,
                                                    },
                                                }}
                                                onMouseEnter={() =>
                                                    setRowHover(props.data.tableData.id)
                                                }
                                                onMouseLeave={() => setRowHover('')}
                                            />
                                        );
                                    },
                                    Action: function ActionComponent(props) {
                                        const { data } = props;
                                        const { id, removed, tableData } = data;
                                        return (
                                            <Grid container justifyContent="center">
                                                {tableData.id === rowHover && removed === false
                                                    ? (
                                                          <Tooltip title="Revoke link" placement="top">
                                                              <IconButton onClick={() => handleUnlinkProfile(id)}>
                                                                  <DeleteIcon className={classes.deleteIcon} />
                                                              </IconButton>
                                                          </Tooltip>
                                                      )
                                                    : removed && (
                                                          <Button size="small" onClick={() => handleUndoProfile(data)}>
                                                              UNDO
                                                          </Button>
                                                      )}
                                            </Grid>
                                        );
                                    },
                                }}
                                isLoading={loading}
                            />
                        </TableContainer>
                    </Grid>
                    <Grid item>
                        <Grid container justifyContent="flex-end" alignItems="right">
                            {selectionMade && (
                                <Grid item>
                                    <Tooltip
                                        title="Save the above changes."
                                        placement="top"
                                    >
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            size="small"
                                            onClick={handleDelete}
                                        >
                                            Save Changes
                                        </Button>
                                    </Tooltip>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};

export default LinkedProfiles;
